import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { APP_VERSION, BUILD_ID } from '../utils/version';
import { goBack } from '../utils/navigation';
import GoogleSignInButton from '../components/GoogleSignInButton';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const googleRef = useRef(null);

  useEffect(() => {
    if (error?.action === 'google_login') googleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Capturar redirectUrl ANTES del login para evitar que se pierda
    const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
    if (redirectUrl) {
      sessionStorage.removeItem('redirectAfterLogin');
    }

    try {
      const userData = await login(email, password);
      
      if (redirectUrl && redirectUrl !== '/login') {
        navigate(redirectUrl, { replace: true });
        return;
      }
      
      // Usar setTimeout para evitar race conditions con React Router
      setTimeout(() => {
        // Admin y Administracion van al panel de administración
        if (userData.rol === 'admin' || userData.rol === 'administracion' || userData.rol === 'superadmin') {
          navigate('/admin/dashboard', { replace: true });
        } else {
          // Usuarios normales van a la página principal con las 3 opciones
          navigate('/', { replace: true });
        }
      }, 100);
    } catch (err) {
      // Si falla el login, restaurar el redirectUrl
      if (redirectUrl) {
        sessionStorage.setItem('redirectAfterLogin', redirectUrl);
      }

      // El backend puede devolver el detail como STRING (mensaje simple) o
      // como OBJETO { code, message, action } para diferenciar
      // 'email_not_found' vs 'wrong_password' y guiar al usuario con un
      // link a /register o al reset de contraseña.
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        setError({ message: detail.message, action: detail.action || null });
      } else if (typeof detail === 'string') {
        setError({ message: detail, action: null });
      } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError({ message: 'Error de conexión. Verificá tu conexión a internet e intentá de nuevo.', action: null });
      } else if (err.response?.status === 500) {
        setError({ message: 'Error del servidor. Por favor intentá de nuevo en unos minutos.', action: null });
      } else if (err.response?.status === 401) {
        setError({ message: 'Credenciales inválidas. Verificá tu email y contraseña.', action: null });
      } else {
        setError({ message: 'Error al iniciar sesión. Por favor intentá de nuevo.', action: null });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8 px-4 overflow-x-hidden">
      {/* Skip link para accesibilidad */}
      <a href="#login-form" className="skip-link">
        Ir al formulario de inicio de sesión
      </a>
      
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/logo-colegio.png"
            alt="Logo del Colegio de Trabajadorxs Sociales de Lomas de Zamora"
            className="h-32 sm:h-40 w-auto max-w-[200px] sm:max-w-none object-contain"
            data-testid="logo-colegio"
            loading="eager"
          />
        </div>

        {/* Descripción de la app */}
        <div className="text-center mb-6 px-2">
          <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-100 rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-700 leading-relaxed">
              👋 <strong className="text-primary">¡Bienvenide!</strong> Este es el espacio de 
              <strong> Servicios Digitales</strong> del 
              <strong> Colegio Profesional de Trabajadorxs Sociales de Lomas de Zamora</strong>. 
              Accedé al Recursero del Distrito, Capacitaciones, Guía de Beneficios TS+, Estado de Cuenta, Listado de Auxiliares de Justicia y mucho más.
            </p>
          </div>
        </div>

        {/* Recuadro de registro - Primera vez */}
        <div className="mb-4 px-2">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm text-center">
            <p className="text-base font-bold text-gray-800 mb-2">¿Ingresás por primera vez?</p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors"
              data-testid="register-link-top"
            >
              Registrate acá
            </Link>
          </div>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl" id="login-title">Ingrese sus datos</CardTitle>
            <CardDescription>
              Sistema de Gestión del CATSLZ
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className="mb-4 flex items-start gap-3 p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-900"
              role="note"
              data-testid="warning-externo-campus"
            >
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
              <p className="text-sm leading-snug">
                <strong>¿Sos usuario externo del CATSLZ y querés entrar al Campus Virtual?</strong>{' '}
                Este login es para <strong>matriculadxs de Lomas de Zamora</strong>.
                Para el Campus, ingresá desde{' '}
                <a
                  href="/campus/acceso"
                  className="font-semibold underline hover:text-amber-700"
                  data-testid="link-campus-acceso"
                >
                  este link
                </a>
                .
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" id="login-form" aria-labelledby="login-title">
              {error && (
                <div 
                  className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" 
                  data-testid="login-error"
                  role="alert"
                  aria-live="polite"
                >
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="flex-1">
                    <span>{error.message}</span>
                    {error.action === 'register' && (
                      <>
                        {' '}
                        <Link
                          to="/register"
                          className="underline font-semibold text-red-800 hover:text-red-900"
                          data-testid="login-error-link-register"
                        >
                          Registrarme
                        </Link>
                        .
                      </>
                    )}
                    {error.action === 'reset_password' && (
                      <>
                        {' '}
                        <Link
                          to="/reset-password"
                          className="underline font-semibold text-red-800 hover:text-red-900"
                          data-testid="login-error-link-reset"
                        >
                          Restablecer contraseña
                        </Link>
                        .
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="input-with-icon">
                  <Mail className="icon-left w-4 h-4" aria-hidden="true" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10"
                    data-testid="login-email-input"
                    aria-describedby="email-hint"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="input-with-icon has-right-icon">
                  <Lock className="icon-left w-4 h-4" aria-hidden="true" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 pr-10"
                    data-testid="login-password-input"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="icon-right cursor-pointer hover:text-gray-600"
                    data-testid="toggle-password"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>

              {/* Separador + botón oficial de Google */}
              <div className="relative my-1">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2 text-gray-500">o continuá con</span>
                </div>
              </div>
              <div
                ref={googleRef}
                className={`rounded-lg transition-shadow duration-300 ${error?.action === 'google_login' ? 'ring-4 ring-blue-400 ring-offset-2 animate-pulse' : ''}`}
                data-testid="login-google-wrapper"
              >
                <GoogleSignInButton />
              </div>
              <div className="text-center">
                <Link
                  to="/recuperar-password"
                  className="text-sm text-gray-500 hover:text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </form>

          </CardContent>
        </Card>

        <div className="text-center mt-6 mb-8 space-y-3">
          <div className="flex justify-center">
            <button 
              onClick={() => goBack()}
              className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              data-testid="back-to-services-btn"
            >
              ← Volver
            </button>
          </div>
          <p className="text-xs text-gray-400 pb-4">
            Colegio de Trabajadorxs Sociales - Distrito Lomas de Zamora
          </p>
          <p className="text-[10px] text-gray-300 font-mono" data-testid="app-version">
            v{APP_VERSION} ({BUILD_ID})
          </p>
        </div>
      </div>
    </div>
  );
}
